import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useReducedMotion } from './useReducedMotion';
import './CollectionPresence.css';

/** Match the local opacity animation so outgoing content retires after its exit. */
const FADE_DURATION_MS = 160;

/** Properties for a stationary collection handoff. */
interface CollectionPresenceProps {
  /** Change when the collection's membership or empty state changes. */
  contentKey: string;
  children: ReactNode;
  className?: string;
}

/** One visual copy of the immediately preceding collection. */
interface OutgoingCollection {
  children: ReactNode;
  generation: number;
}

/**
 * Crossfade a collection without moving its records or delaying new actions.
 * The current layer never changes identity; the outgoing copy cannot receive
 * focus or pointer input and is excluded from the accessibility tree.
 */
export function CollectionPresence({ contentKey, children, className = '' }: CollectionPresenceProps) {
  const reduced = useReducedMotion();
  const committed = useRef({ contentKey, children });
  const generation = useRef(0);
  const [outgoing, setOutgoing] = useState<OutgoingCollection | null>(null);

  useLayoutEffect(() => {
    const previous = committed.current;
    committed.current = { contentKey, children };
    if (reduced) {
      setOutgoing(null);
    } else if (previous.contentKey !== contentKey) {
      generation.current += 1;
      setOutgoing({ children: previous.children, generation: generation.current });
    }
  }, [contentKey, children, reduced]);

  useLayoutEffect(() => {
    if (!outgoing || reduced) return undefined;
    const retiring = outgoing.generation;
    const timer = setTimeout(() => {
      setOutgoing(current => current?.generation === retiring ? null : current);
    }, FADE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [outgoing, reduced]);

  // Alternate animation names to restart an interrupted fade without keying
  // the live wrapper, which would replace focused controls and their state.
  const fade = outgoing && !reduced ? (outgoing.generation % 2 ? 'a' : 'b') : undefined;

  return (
    <div className={`collection-presence ${className}`}>
      <div className="collection-presence-current" data-fade={fade}>
        {children}
      </div>
      {outgoing && !reduced && (
        <div key={outgoing.generation} className="collection-presence-outgoing" aria-hidden="true" inert style={{ pointerEvents: 'none' }}>
          {outgoing.children}
        </div>
      )}
    </div>
  );
}
